import React, { useState } from 'react';
import { CheckCircle, XCircle, AlertCircle, Clock, ChevronDown, ChevronRight } from 'lucide-react';
import styles from './TabValidationStatus.module.css';

interface TabValidation {
  is_complete: boolean;
  is_approved: boolean;
  issues: string[];
  warnings?: string[];
  status: 'incomplete' | 'needs_approval' | 'approved';
  [key: string]: any; // For additional tab-specific data
}

interface TabValidationStatusProps {
  tabName: string;
  validation: TabValidation;
  onApprove: () => void;
}

export const TabValidationStatus: React.FC<TabValidationStatusProps> = ({
  tabName,
  validation,
  onApprove
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getStatusIcon = () => {
    if (validation.status === 'approved') {
      return <CheckCircle className={`${styles.statusIcon} ${styles.approved}`} />;
    } else if (validation.status === 'incomplete') {
      return <XCircle className={`${styles.statusIcon} ${styles.incomplete}`} />;
    } else {
      return <Clock className={`${styles.statusIcon} ${styles.pending}`} />;
    }
  };

  const getStatusText = () => {
    if (validation.status === 'approved') {
      return 'Approved';
    } else if (validation.status === 'incomplete') {
      return 'Incomplete';
    } else {
      return 'Needs Approval';
    }
  };

  const issuesCount = validation.issues?.length || 0;
  const warningsCount = validation.warnings?.length || 0;
  const totalCount = issuesCount + warningsCount;

  return (
    <div className={styles.tabValidationStatus}>
      <div 
        className={`${styles.validationHeader} ${styles.clickable}`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className={styles.statusIndicator}>
          {isExpanded ? <ChevronDown className={styles.chevronIcon} /> : <ChevronRight className={styles.chevronIcon} />}
          {getStatusIcon()}
          <span className={styles.statusText}>{getStatusText()}</span>
          {totalCount > 0 && (
            <span className={styles.countBadge}>
              {issuesCount > 0 && <span className={styles.issuesCount}>{issuesCount} issue{issuesCount !== 1 ? 's' : ''}</span>}
              {warningsCount > 0 && <span className={styles.warningsCount}>{warningsCount} warning{warningsCount !== 1 ? 's' : ''}</span>}
            </span>
          )}
        </div>
        {validation.status === 'needs_approval' && (
          <button 
            className={styles.approveButton}
            onClick={(e) => {
              e.stopPropagation();
              onApprove();
            }}
          >
            Approve {tabName}
          </button>
        )}
      </div>

      {isExpanded && (
        <>
          {validation.issues && validation.issues.length > 0 && (
            <div className={styles.validationIssues}>
              <h4>Issues:</h4>
              <ul>
                {validation.issues.map((issue, index) => (
                  <li key={index}>{issue}</li>
                ))}
              </ul>
            </div>
          )}

          {validation.warnings && validation.warnings.length > 0 && (
            <div className={styles.validationWarnings}>
              <h4>Warnings:</h4>
              <ul>
                {validation.warnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            </div>
          )}

          {validation.status === 'approved' && (
            <div className={styles.validationSuccess}>
              <CheckCircle className={styles.successIcon} />
              <span>{tabName} has been approved</span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

